import { type Component, type FieldDTOV1, FieldTypeEnum, EntityFieldCodeEnum } from '@/common/types/fields'
import { EntityEnum } from '@/common/types/general'
import { type MainEntity } from '@/common/types/mainEntity'
import { type ColumnDefinition } from '@/search/types/resultViewConfig'
import { type QTableProps } from 'quasar'

export const entitiesAvailable = [EntityEnum.TICKET, EntityEnum.CUSTOMER, EntityEnum.COMPANY, EntityEnum.INTERACTION]

/**
 * All panels displayable in search
 */
export enum PanelEnum {
  SearchForm = 'SearchForm', // Criteria
  SearchResult = 'SearchResult', // Result
}

/**
 * All operations available for criteria
 */
export enum OperationEnum {
  eq = 'eq',
  in = 'in',
  gt = 'gt',
  ge = 'ge',
  lt = 'lt',
  le = 'le',
  custom = 'custom',
}

/**
 * Specific Wrapper for the search
 * defaultOperation is the default operation for each criteria
 * operation is to define a specific operation for each criteria
 * options include list of possible values of field, if applicable. Used when we apply a search filter.
 * customMethodOnApplyFilter is a custom method to set the values on the component when we apply a filter
 * replaceCustomTagsByValues is a custom method to replace custom tags by good values before to apply the search
 */
export interface SearchFieldWrapper {
  field: FieldDTOV1
  readonly defaultValue: string[]
  currentValue: string[]
  component: Component
  readonly defaultOperation: OperationEnum
  operation: OperationEnum
  options?: string[]
  customMethodOnApplyFilter?: (values: string[]) => void
  replaceCustomTagsByValues?: (values: string[]) => string[]
  disabled?: boolean
}

/**
 * Map of SearchFieldWrapper with code as key
 */
export type Criteria = {
  [code: string]: SearchFieldWrapper
}

/**
 * Map of Criteria used By GroupedCriteria with code as key
 */
export type GroupedCriteria = {
  [code: string]: SearchFieldWrapper
}

/**
 * List of grouped criteria
 */
export enum GroupedCriteriaParentEnum {
  CUSTOMER_DELETED = 'CUSTOMER_DELETED',
  OTHER_TICKET_ACTION = 'OTHER_TICKET_ACTION',
  TICKET_ACTION_REASON = 'TICKET_ACTION_REASON',
  TICKET_IMPORT = 'TICKET_IMPORT',
}

/**
 * Search read modes
 * CRITERIA = tooltip of criteria used
 * TABLE = cell in search result table
 */
export const enum ReadModeEnum {
  CRITERIA = 'CRITERIA',
  TABLE = 'TABLE',
}

/**
 * All placements available
 */
export const enum FieldsPlacementEnum {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  GROUP = 'GROUP',
}

/**
 * Those two types are important to defined the next type : fieldsPlacementObject
 */
type KeysFieldsPlacement<K extends string> = Record<K, Array<EntityFieldCodeEnum | GroupedCriteriaParentEnum>>
type KeysEntity<K extends string> = Record<K, KeysFieldsPlacement<FieldsPlacementEnum>>
type KeysSearchStoreId<K extends string> = Record<K, KeysEntity<EntityEnum>>

const defaultPlacement: KeysEntity<EntityEnum> = {
  TICKET: {
    LEFT: [
      EntityFieldCodeEnum.TICKET_ID,
      EntityFieldCodeEnum.EXTERNAL_REF,
      EntityFieldCodeEnum.PARENT_TICKET_ID,
      EntityFieldCodeEnum.CREATION_SUMMARY,
      EntityFieldCodeEnum.CREATION_CHANNEL,
      EntityFieldCodeEnum.CURRENT_CHANNEL,
      EntityFieldCodeEnum.PRIORITY,
      EntityFieldCodeEnum.EMERGENCY,
      EntityFieldCodeEnum.CREATION_DATE,
      EntityFieldCodeEnum.LAST_MODIFIED_DATE,
      EntityFieldCodeEnum.WAKE_UP_DATE,
      EntityFieldCodeEnum.DUE_DATE,
      EntityFieldCodeEnum.DUE_DATE_ALERT,
      EntityFieldCodeEnum.TICKET_SLA,
    ],
    RIGHT: [
      EntityFieldCodeEnum.TICKET_STATUS,
      EntityFieldCodeEnum.VALIDATION_STATUS,
      EntityFieldCodeEnum.INITIAL_SKILL,
      EntityFieldCodeEnum.CURRENT_SKILL,
      EntityFieldCodeEnum.TYPOLOGY,
      EntityFieldCodeEnum.CREATION_USER,
      EntityFieldCodeEnum.ASSIGNEE,
      EntityFieldCodeEnum.TICKET_USERGROUP,
      EntityFieldCodeEnum.CREATION_SCREEN,
      EntityFieldCodeEnum.ONCE_AND_DONE,
      EntityFieldCodeEnum.TICKET_MAILBOX,
      EntityFieldCodeEnum.HAS_ATTACHMENTS,
      EntityFieldCodeEnum.HAS_CUSTOMER,
    ],
    GROUP: [GroupedCriteriaParentEnum.TICKET_IMPORT, GroupedCriteriaParentEnum.OTHER_TICKET_ACTION, GroupedCriteriaParentEnum.TICKET_ACTION_REASON],
  },
  CUSTOMER: {
    LEFT: [
      EntityFieldCodeEnum.CUSTOMER_ID,
      EntityFieldCodeEnum.CUSTOMER_EMAILS,
      EntityFieldCodeEnum.CUSTOMER_CREATIONDATE,
      EntityFieldCodeEnum.CUSTOMER_LAST_MODIFIED_DATE,
      EntityFieldCodeEnum.CUSTOMER_ADDRESS,
      EntityFieldCodeEnum.CUSTOMER_COMMENTS,
    ],
    RIGHT: [
      EntityFieldCodeEnum.CUSTOMER_REF,
      EntityFieldCodeEnum.CUSTOMER_PHONE,
      EntityFieldCodeEnum.CUSTOMER_LASTNAME,
      EntityFieldCodeEnum.CUSTOMER_FIRSTNAME,
      EntityFieldCodeEnum.CUSTOMER_COMPANYNAME,
      EntityFieldCodeEnum.CUSTOMER_VIP,
      EntityFieldCodeEnum.CUSTOMER_NIR,
    ],
    GROUP: [GroupedCriteriaParentEnum.CUSTOMER_DELETED],
  },
  INTERACTION: {
    LEFT: [
      EntityFieldCodeEnum.INTERACTION_ID,
      EntityFieldCodeEnum.INTERACTION_TYPE,
      EntityFieldCodeEnum.INTERACTION_DATE,
      EntityFieldCodeEnum.INTERACTION_STATUS,
      EntityFieldCodeEnum.INTERACTION_CONTACT,
    ],
    RIGHT: [
      EntityFieldCodeEnum.INTERACTION_SUMMARY,
      EntityFieldCodeEnum.INTERACTION_CONTENT,
      EntityFieldCodeEnum.INTERACTION_USER,
      EntityFieldCodeEnum.INTERACTION_SLA,
      EntityFieldCodeEnum.INTERACTION_HAS_ATTACHMENTS,
    ],
    GROUP: [],
  },
  COMPANY: {
    LEFT: [
      EntityFieldCodeEnum.COMPANY_ID,
      EntityFieldCodeEnum.COMPANY_NAME,
      EntityFieldCodeEnum.COMPANY_ADDRESS,
      EntityFieldCodeEnum.COMPANY_ZIP_CODE,
      EntityFieldCodeEnum.COMPANY_CREATIONDATE,
      EntityFieldCodeEnum.COMPANY_LAST_MODIFIED_DATE,
      EntityFieldCodeEnum.COMPANY_PHONE1,
      EntityFieldCodeEnum.COMPANY_EMAIL1,
    ],
    RIGHT: [
      EntityFieldCodeEnum.COMPANY_EXTERNAL_REF,
      EntityFieldCodeEnum.COMPANY_COUNTRY,
      EntityFieldCodeEnum.COMPANY_CITY,
      EntityFieldCodeEnum.COMPANY_PHONE2,
      EntityFieldCodeEnum.COMPANY_EMAIL2,
    ],
    GROUP: [],
  },
}
/**
 * Schema of entity fields disposition in search form panel
 * Keep the order, it is important
 */
export const fieldsPlacementObject: KeysSearchStoreId<SearchStoreIdEnum> = {
  MAIN: defaultPlacement,
  CHANGE_CUSTOMER: {
    TICKET: defaultPlacement.TICKET,
    CUSTOMER: { ...defaultPlacement.CUSTOMER, GROUP: [] },
    INTERACTION: defaultPlacement.INTERACTION,
    COMPANY: defaultPlacement.COMPANY,
  },
  CHANGE_COMPANY: {
    TICKET: defaultPlacement.TICKET,
    CUSTOMER: defaultPlacement.CUSTOMER,
    INTERACTION: defaultPlacement.INTERACTION,
    COMPANY: { ...defaultPlacement.COMPANY, GROUP: [] },
  },
  ATTACH_CUSTOMER_TO_COMPANY: {
    TICKET: defaultPlacement.TICKET,
    CUSTOMER: { ...defaultPlacement.CUSTOMER, GROUP: [] },
    INTERACTION: defaultPlacement.INTERACTION,
    COMPANY: defaultPlacement.COMPANY,
  },
  ATTACH_CASE_TO_COMPANY: {
    TICKET: { ...defaultPlacement.TICKET, GROUP: [] },
    CUSTOMER: defaultPlacement.CUSTOMER,
    INTERACTION: defaultPlacement.INTERACTION,
    COMPANY: defaultPlacement.COMPANY,
  },
  COMPANY_CASES: {
    TICKET: defaultPlacement.TICKET,
    CUSTOMER: defaultPlacement.CUSTOMER,
    INTERACTION: defaultPlacement.INTERACTION,
    COMPANY: {
      LEFT: [EntityFieldCodeEnum.COMPANY_ID, ...defaultPlacement.COMPANY.LEFT],
      RIGHT: defaultPlacement.COMPANY.RIGHT,
      GROUP: defaultPlacement.COMPANY.GROUP,
    },
  },
  COMPANY_CUSTOMERS: {
    TICKET: defaultPlacement.TICKET,
    CUSTOMER: defaultPlacement.CUSTOMER,
    INTERACTION: defaultPlacement.INTERACTION,
    COMPANY: {
      LEFT: [EntityFieldCodeEnum.COMPANY_ID, ...defaultPlacement.COMPANY.LEFT],
      RIGHT: defaultPlacement.COMPANY.RIGHT,
      GROUP: defaultPlacement.COMPANY.GROUP,
    },
  },
  ATTACHPARENT: {
    TICKET: {
      LEFT: [EntityFieldCodeEnum.EXTERNAL_REF],
      RIGHT: [EntityFieldCodeEnum.TICKET_ID],
      GROUP: [],
    },
    CUSTOMER: {
      LEFT: [],
      RIGHT: [],
      GROUP: [],
    },
    INTERACTION: {
      LEFT: [],
      RIGHT: [],
      GROUP: [],
    },
    COMPANY: defaultPlacement.COMPANY,
  },
  LINKS: defaultPlacement,
}

/**
 * Model of field or criteria used by SearchRequest
 */
export type FieldSearchRequest = {
  code: string
  op: OperationEnum
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  values: Array<{}>
}

/**
 * Model to ask server what we want to search
 */
export type SearchDTOV1 = {
  entity: EntityEnum
  max: number
  fields: FieldSearchRequest[]
  criteria: FieldSearchRequest[]
  sort: string[]
}

export const EntityFieldsDefaultOperationMap = new Map<string, OperationEnum>([
  // TICKET
  [EntityFieldCodeEnum.EXTERNAL_REF, OperationEnum.gt],
  [EntityFieldCodeEnum.CREATION_USER, OperationEnum.in],
  [EntityFieldCodeEnum.ASSIGNEE, OperationEnum.in],
  [EntityFieldCodeEnum.CREATION_SUMMARY, OperationEnum.in],
  [EntityFieldCodeEnum.CREATION_SCREEN, OperationEnum.in],
  // custom
  [EntityFieldCodeEnum.HAS_ATTACHMENTS, OperationEnum.custom],
  [EntityFieldCodeEnum.VALIDATION_STATUS, OperationEnum.custom],
  [EntityFieldCodeEnum.TICKET_SLA, OperationEnum.custom],
  [EntityFieldCodeEnum.HAS_CUSTOMER, OperationEnum.custom],

  // CUSTOMER
  [EntityFieldCodeEnum.CUSTOMER_REF, OperationEnum.gt],
  [EntityFieldCodeEnum.CUSTOMER_LASTNAME, OperationEnum.gt],
  [EntityFieldCodeEnum.CUSTOMER_FIRSTNAME, OperationEnum.gt],
  [EntityFieldCodeEnum.CUSTOMER_COMPANYNAME, OperationEnum.gt],
  [EntityFieldCodeEnum.CUSTOMER_COMMENTS, OperationEnum.in],
  // custom
  [EntityFieldCodeEnum.CUSTOMER_EMAILS, OperationEnum.custom],
  [EntityFieldCodeEnum.CUSTOMER_PHONE, OperationEnum.custom],
  [EntityFieldCodeEnum.CUSTOMER_ADDRESS, OperationEnum.custom],

  // INTERACTION
  [EntityFieldCodeEnum.INTERACTION_SLA, OperationEnum.custom],
  [EntityFieldCodeEnum.INTERACTION_TYPE, OperationEnum.custom],
  [EntityFieldCodeEnum.INTERACTION_HAS_ATTACHMENTS, OperationEnum.custom],
  [EntityFieldCodeEnum.INTERACTION_CONTACT, OperationEnum.custom],
  [EntityFieldCodeEnum.INTERACTION_CONTENT, OperationEnum.custom],

  //COMPANY
  [EntityFieldCodeEnum.COMPANY_ADDRESS, OperationEnum.in],
  [EntityFieldCodeEnum.COMPANY_NAME, OperationEnum.gt],
  [EntityFieldCodeEnum.COMPANY_EXTERNAL_REF, OperationEnum.gt],
])

export const FieldTypesDefaultOperationMap = new Map<FieldTypeEnum, OperationEnum>([
  [FieldTypeEnum.TEXT, OperationEnum.eq],
  [FieldTypeEnum.TEXTAREA, OperationEnum.eq],
  [FieldTypeEnum.EMAIL, OperationEnum.eq],
  [FieldTypeEnum.NUMBER, OperationEnum.eq],
  [FieldTypeEnum.LOCAL_DATE, OperationEnum.in],
  [FieldTypeEnum.TIMESTAMP, OperationEnum.in],
  [FieldTypeEnum.SELECT_LIST_SINGLE, OperationEnum.in],
  [FieldTypeEnum.SELECT_LIST_MULTIPLE, OperationEnum.eq],
  [FieldTypeEnum.CHECKBOX_SINGLE, OperationEnum.eq],
  [FieldTypeEnum.CHECKBOX_MULTIPLE, OperationEnum.eq],
  [FieldTypeEnum.RADIO, OperationEnum.eq],
  [FieldTypeEnum.TREE_SINGLE, OperationEnum.eq],
  [FieldTypeEnum.TREE_MULTIPLE, OperationEnum.eq],
  [FieldTypeEnum.TREE_SINGLE_ASCENDING, OperationEnum.eq],
])

/**
 * Available operations for an input for each type
 */
export const AvailableOperationsMap = new Map<FieldTypeEnum, OperationEnum[]>([
  [FieldTypeEnum.TEXT, [OperationEnum.eq, OperationEnum.in, OperationEnum.gt, OperationEnum.lt]],
  [FieldTypeEnum.TEXTAREA, [OperationEnum.eq, OperationEnum.in, OperationEnum.gt, OperationEnum.lt]],
  [FieldTypeEnum.EMAIL, [OperationEnum.eq, OperationEnum.in, OperationEnum.gt, OperationEnum.lt]],
  [FieldTypeEnum.NUMBER, [OperationEnum.eq, OperationEnum.ge, OperationEnum.le]],
  [FieldTypeEnum.LOCAL_DATE, [OperationEnum.in, OperationEnum.le, OperationEnum.ge]],
  [FieldTypeEnum.TIMESTAMP, [OperationEnum.in, OperationEnum.le, OperationEnum.ge]],
])

export const columnNameChannelIcon = 'channelIcon'
export const columnNameItemActions = 'itemActions'
export const defaultPage: number = 1
export const rowsPerPageByDefault: number = 25

/**
 * All fields used as specific criteria for request search v2
 */
export const SpecialFieldCriteria: string[] = [
  EntityFieldCodeEnum.CUSTOMER_EMAILS,
  EntityFieldCodeEnum.CUSTOMER_PHONE,
  EntityFieldCodeEnum.CUSTOMER_ADDRESS,
  EntityFieldCodeEnum.HAS_CUSTOMER,
  EntityFieldCodeEnum.HAS_ATTACHMENTS,
  EntityFieldCodeEnum.INTERACTION_TYPE,
  EntityFieldCodeEnum.VALIDATION_STATUS,
  EntityFieldCodeEnum.TICKET_SLA,
  EntityFieldCodeEnum.INTERACTION_SLA,
  EntityFieldCodeEnum.INTERACTION_HAS_ATTACHMENTS,
  EntityFieldCodeEnum.INTERACTION_CONTACT,
  EntityFieldCodeEnum.INTERACTION_CONTENT,
  GroupedCriteriaParentEnum.OTHER_TICKET_ACTION,
  GroupedCriteriaParentEnum.TICKET_ACTION_REASON,
  GroupedCriteriaParentEnum.TICKET_IMPORT,
  GroupedCriteriaParentEnum.CUSTOMER_DELETED,
]

/**
 * Map of fields with their grouped criteria (the order is important)
 */
export const FieldsUsedByGroupedCriteriaMap = new Map<string, GroupedCriteriaParentEnum>([
  [EntityFieldCodeEnum.IMPORT_ID, GroupedCriteriaParentEnum.TICKET_IMPORT],
  [EntityFieldCodeEnum.IMPORT_USER, GroupedCriteriaParentEnum.TICKET_IMPORT],
  [EntityFieldCodeEnum.IMPORT_ACTION_TYPE, GroupedCriteriaParentEnum.TICKET_IMPORT],
  [EntityFieldCodeEnum.IMPORT_DATE, GroupedCriteriaParentEnum.TICKET_IMPORT],
  [EntityFieldCodeEnum.OTHER_ACTION_TYPE, GroupedCriteriaParentEnum.OTHER_TICKET_ACTION],
  [EntityFieldCodeEnum.OTHER_ACTION_DATE, GroupedCriteriaParentEnum.OTHER_TICKET_ACTION],
  [EntityFieldCodeEnum.OTHER_ACTION_USER, GroupedCriteriaParentEnum.OTHER_TICKET_ACTION],
  [EntityFieldCodeEnum.ACTION_REASON, GroupedCriteriaParentEnum.TICKET_ACTION_REASON],
  [EntityFieldCodeEnum.REASON_LAST_ACTION_ONLY, GroupedCriteriaParentEnum.TICKET_ACTION_REASON],
  [EntityFieldCodeEnum.CUSTOMER_INCLUDE_DELETED, GroupedCriteriaParentEnum.CUSTOMER_DELETED],
  [EntityFieldCodeEnum.CUSTOMER_DELETEDATE, GroupedCriteriaParentEnum.CUSTOMER_DELETED],
])

/**
 * Map of FieldData for grouped criteria
 */
export const GroupedCriteriaParentEntityEnumMap = new Map<GroupedCriteriaParentEnum, EntityEnum>([
  [GroupedCriteriaParentEnum.TICKET_IMPORT, EntityEnum.TICKET],
  [GroupedCriteriaParentEnum.OTHER_TICKET_ACTION, EntityEnum.TICKET],
  [GroupedCriteriaParentEnum.TICKET_ACTION_REASON, EntityEnum.TICKET],
  [GroupedCriteriaParentEnum.CUSTOMER_DELETED, EntityEnum.CUSTOMER],
])

/**
 * All import action type options
 */
export enum ImportActionTypeEnum {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  IDENTIFIED = 'IDENTIFIED',
}

/**
 * Search export endpoints enum
 */

export enum ExportEndpointEnum {
  CASES = 'cases',
  CUSTOMERS = 'customers',
  INTERACTIONS = 'interactions',
}

type Unpacked<T> = T extends (infer U)[] ? U : T
export type SearchResultColumn = Unpacked<QTableProps['columns']> & { columnDefinition?: ColumnDefinition }

export type SearchFilterDTOV1 = {
  id: UUID
  name: string
  entity: EntityEnum
  shared: boolean
  creationUser: string
  fields: SearchFilterField[]
}

export type SearchFilterField = {
  code: string
  values: string[]
  op: OperationEnum
}

export type EntitySearchable = MainEntity

/**
 * All user token available for selection (disabled resources included)
 */
export enum UserSelectTokenEnumDisabledIncluded {
  EVERYONE_DISABLED_INCLUDED = 'EVERYONE_DISABLED_INCLUDED',
  ME = 'ME',
  MY_TEAM_DISABLED_INCLUDED = 'MY_TEAM_DISABLED_INCLUDED',
  MY_OTHER_TEAMS_DISABLED_INCLUDED = 'MY_OTHER_TEAMS_DISABLED_INCLUDED',
}

/**
 * All user token available for selection (enabled resources only)
 */
export enum UserSelectTokenEnumEnabledOnly {
  EVERYONE_ENABLED_ONLY = 'EVERYONE_ENABLED_ONLY',
  ME = 'ME',
  MY_TEAM_ENABLED_ONLY = 'MY_TEAM_ENABLED_ONLY',
  MY_OTHER_TEAMS_ENABLED_ONLY = 'MY_OTHER_TEAMS_ENABLED_ONLY',
}

export type UserTokenValue = UserSelectTokenEnumDisabledIncluded | UserSelectTokenEnumEnabledOnly

/**
 * Map of equivalence tokens between disabled and enabled resources
 */
export const UserTokenEquivalenceMap = new Map<UserTokenValue, UserTokenValue>([
  [UserSelectTokenEnumDisabledIncluded.EVERYONE_DISABLED_INCLUDED, UserSelectTokenEnumEnabledOnly.EVERYONE_ENABLED_ONLY],
  [UserSelectTokenEnumDisabledIncluded.ME, UserSelectTokenEnumEnabledOnly.ME],
  [UserSelectTokenEnumDisabledIncluded.MY_TEAM_DISABLED_INCLUDED, UserSelectTokenEnumEnabledOnly.MY_TEAM_ENABLED_ONLY],
  [UserSelectTokenEnumDisabledIncluded.MY_OTHER_TEAMS_DISABLED_INCLUDED, UserSelectTokenEnumEnabledOnly.MY_OTHER_TEAMS_ENABLED_ONLY],
  [UserSelectTokenEnumEnabledOnly.EVERYONE_ENABLED_ONLY, UserSelectTokenEnumDisabledIncluded.EVERYONE_DISABLED_INCLUDED],
  [UserSelectTokenEnumEnabledOnly.ME, UserSelectTokenEnumDisabledIncluded.ME],
  [UserSelectTokenEnumEnabledOnly.MY_TEAM_ENABLED_ONLY, UserSelectTokenEnumDisabledIncluded.MY_TEAM_DISABLED_INCLUDED],
  [UserSelectTokenEnumEnabledOnly.MY_OTHER_TEAMS_ENABLED_ONLY, UserSelectTokenEnumDisabledIncluded.MY_OTHER_TEAMS_DISABLED_INCLUDED],
])

export enum SearchStoreIdEnum {
  MAIN = 'MAIN',
  CHANGE_CUSTOMER = 'CHANGE_CUSTOMER',
  CHANGE_COMPANY = 'CHANGE_COMPANY',
  ATTACH_CUSTOMER_TO_COMPANY = 'ATTACH_CUSTOMER_TO_COMPANY',
  ATTACH_CASE_TO_COMPANY = 'ATTACH_CASE_TO_COMPANY',
  LINKS = 'LINKS',
  ATTACHPARENT = 'ATTACHPARENT',
  COMPANY_CASES = 'COMPANY_CASES',
  COMPANY_CUSTOMERS = 'COMPANY_CUSTOMERS',
}

export enum SearchLinkEventScopeEnum {
  CASE_IMPORT = 'CASE_IMPORT',
  CASE_REJECTION = 'CASE_REJECTION',
  CUSTOMER_DUPLICATED = 'CUSTOMER_DUPLICATED',
  CASE_DUPLICATED = 'CASE_DUPLICATED',
  STOCK_SUPERVISION = 'STOCK_SUPERVISION',
  CUSTOMER_CASES = 'CUSTOMER_CASES',
  COMPANY_CASES = 'COMPANY_CASES',
  CHILD_CASES = 'CHILD_CASES',
  TEAM_CASES = 'TEAM_CASES',
  CUSTOMER_CTI_PHONE = 'CUSTOMER_CTI_PHONE',
}

export interface SearchLinkEvent {
  scope: SearchLinkEventScopeEnum
  payload: {
    fileImportId?: number
    importActionType?: ImportActionTypeEnum
    externalRef?: string
    customerRef?: string
    emailAddress?: string
    nir?: string
    duePeriodMax?: string
    duePeriodMin?: string
    skillCode?: string[]
    customerId?: number
    companyId?: number
    parentId?: number
    teamCode?: UUID
    customerPhone?: string
  }
  preFillForm?: boolean
}

export const FieldCodesAvailableForSpecificSearch = new Map<SearchStoreIdEnum, EntityFieldCodeEnum[]>([
  [SearchStoreIdEnum.ATTACHPARENT, [EntityFieldCodeEnum.EXTERNAL_REF, EntityFieldCodeEnum.TICKET_ID]],
])

export type ScopeForAttachParent = {
  spaceCode: UUID
  caseIds: number[]
  originalSearchStore: SearchStoreIdEnum
  reloadLegacyCaseList?: boolean
}

/**
 * All due date search mode
 */
export enum DueDateSearchModeEnum {
  DUE_DATE_OK = 'DUE_DATE_OK',
  DUE_DATE_WARNING = 'DUE_DATE_WARNING',
  DUE_DATE_NOK = 'DUE_DATE_NOK',
}

/**
 * When we apply a bulk action on search v2 when we are currently on a case, we have to reload the page
 * Here are possibles scopes of search v2 in case edition
 */
export const linkScopesRefreshPageOnBulkAction: SearchLinkEventScopeEnum[] = [
  SearchLinkEventScopeEnum.CUSTOMER_CASES,
  SearchLinkEventScopeEnum.CHILD_CASES,
  SearchLinkEventScopeEnum.TEAM_CASES,
]

/**
 * Native fields code which are not allowed to be sorted
 */
export const NativeFieldsSortForbidden: EntityFieldCodeEnum[] = [
  EntityFieldCodeEnum.FIRST_ASSIGNEE,
  EntityFieldCodeEnum.LAST_ASSIGNEE,
  EntityFieldCodeEnum.HAS_ATTACHMENTS,
  EntityFieldCodeEnum.INTERACTION_HAS_ATTACHMENTS,
  EntityFieldCodeEnum.IMPORT_ID,
  EntityFieldCodeEnum.IMPORT_DATE,
  EntityFieldCodeEnum.CUSTOMER_PHONE,
  EntityFieldCodeEnum.CUSTOMER_EMAILS,
]

/**
 * Custom field types which are not allowed to be sorted
 */
export const CustomFieldTypesSortForbidden: FieldTypeEnum[] = [
  FieldTypeEnum.CHECKBOX_MULTIPLE,
  FieldTypeEnum.SELECT_LIST_MULTIPLE,
  FieldTypeEnum.TREE_MULTIPLE,
  FieldTypeEnum.TREE_SINGLE_ASCENDING,
]
